'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useId, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import ChevronIcon from '@/fo/icons/base/icons/chevron.svg';
import { UiModPortalBase } from '@/fo/ui/mod/portal/base';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

import { styles } from './UiAppMenuBase.styles';

type Variants = VariantProps<typeof styles>;

export type MenuItem = {
  level?: number;
  label: string;
  href: string;
  className?: string;
  image?: string;
  image_alt_text?: string;
  children?: MenuItem[];
};

export interface UiAppMenuBaseProps {
  className?: string;
  items: MenuItem[];
  additionalItems?: MenuItem[];
  style?: NativeElement['style'];
  color?: Variants['color'];
  onShow?: () => void;
  onHide?: () => void;
  handleMenuVisibility: (value: boolean) => void;
}

export const STOCK_CLASS_NAME = 'menu-item-stock';

export function UiAppMenuBase({
  className,
  items = [],
  onShow,
  onHide,
  handleMenuVisibility,
  color = 'light',
  ...props
}: UiAppMenuBaseProps) {
  const [activeItem, setActiveItem] = useState<string[]>([]);
  const slots = styles({ color });

  const handleSetActiveItem = (
    newActiveItem: string[],
    children: boolean = false
  ) => {
    setActiveItem(newActiveItem);
    handleMenuVisibility(children);
  };

  return (
    <div
      className={slots.base({ className })}
      {...props}
    >
      <div className={slots.categories()}>
        <Items
          items={items}
          activeItem={activeItem}
          setActiveItem={handleSetActiveItem}
          color={color}
          onShow={() => onShow}
          onHide={() => onHide}
        />
      </div>
    </div>
  );
}

interface ItemsProps {
  classNameItem?: string;
  items?: MenuItem[];
  color?: Variants['color'];
  level?: number;
  isGroup?: boolean;
  activeItem: string[];
  setActiveItem: (key: string[], children?: boolean) => void;
  startIndex?: number;
  onShow?: () => void;
  onHide?: () => void;
}

const Items = ({
  classNameItem,
  items = [],
  level = 0,
  isGroup,
  activeItem,
  color,
  setActiveItem,
  onShow,
  onHide
}: ItemsProps) => {
  const slots = styles({ color });
  const id = useId();
  const refTimer = useRef(0);

  const onActiveKeyChange = (key: string, children: boolean = false) => {
    const nextActiveKey = [...(activeItem || []).slice(0, level), key];
    setActiveItem(nextActiveKey, children);
  };

  const checkChildren = (menuItem: MenuItem) => {
    if (menuItem.level === 0 && menuItem.children && menuItem.children.length) {
      return true;
    }

    if (menuItem.level && menuItem.level > 0) {
      return true;
    }

    return false;
  };

  if (items.length === 0) {
    return null;
  }

  const changeActiveItem = (state: string[]) => {
    clearTimeout(refTimer.current);
    refTimer.current = window.setTimeout(() => {
      setActiveItem(state);
    }, 100);
  };

  const hideMenu = () => {
    changeActiveItem([]);
    onHide?.();
  };
  return (
    <>
      {items &&
        Array.isArray(items) &&
        items.map((item, index) => {
          const menuNode = (
            <>
              <div className={slots.itemLabel()}>
                <span className={item.className}>{item.label}</span>
                {level !== 0 && item.children && (
                  <ChevronIcon className={slots.chevronIcon()} />
                )}
              </div>
            </>
          );

          const itemKey = `${id}-${item.label}-${item.href}-${index}`;
          const groupChildren = (menuItem: MenuItem) => {
            if (
              !menuItem ||
              !menuItem.children ||
              menuItem.children.length === 0
            )
              return [];

            const splitCount = 8;
            const childrenCopy = menuItem.children?.slice();
            const items = [];

            const hasCategories =
              menuItem.children.filter(
                (child) => child.children && child.children.length > 0
              ).length > 0;

            if (hasCategories && menuItem.level === 0) {
              items.push(menuItem.children);
              return items;
            }

            if (!childrenCopy) return [];

            const chunkSize = Math.ceil(Number(childrenCopy.length / 8));

            if (chunkSize > 1) {
              for (let i = 0; i < chunkSize; i++) {
                const chunk = childrenCopy.splice(0, splitCount);

                items.push(chunk);
              }
            } else {
              items.push(menuItem.children);
            }

            return items;
          };

          const childrenGrouped = groupChildren(item);

          const MenuBanner = (
            <Link
              className={slots.itemBanner()}
              href={item.href}
            >
              <Image
                src={
                  item.image
                    ? item.image
                    : '/media/banner/menu-banner-placeholder.webp'
                }
                width={526}
                height={296}
                alt={item.image_alt_text ? item.image_alt_text : item.label}
              />
              <div className={slots.itemBannerText()}>{item.label}</div>
            </Link>
          );

          const childrenNode = activeItem[level] === itemKey &&
            childrenGrouped &&
            childrenGrouped.length > 0 && (
              <div
                className={slots.wrapper()}
                onMouseEnter={onShow}
                onMouseLeave={level < 2 ? () => hideMenu() : () => {}}
              >
                <div className={`${slots.container()} level-${level}`}>
                  {childrenGrouped &&
                    childrenGrouped.map((itemBlock, index) => {
                      return (
                        <div
                          className={slots.itemGrouped()}
                          key={index}
                        >
                          <Items
                            level={level + 1}
                            activeItem={activeItem}
                            setActiveItem={setActiveItem}
                            color={color}
                            items={itemBlock}
                            classNameItem="child"
                          />
                        </div>
                      );
                    })}
                  {childrenGrouped.length <= 2 && level === 0 && MenuBanner}
                  <UiModPortalBase
                    getContainer={() =>
                      document.querySelector(
                        `#UiAppMenuBase-children-${level}>div>div>div`
                      )
                    }
                  >
                    <div
                      id={`UiAppMenuBase-children-${level + 1}`}
                      className={'has-[div]:w-full'}
                    ></div>
                  </UiModPortalBase>
                </div>
              </div>
            );

          return (
            <Link
              key={itemKey}
              href={item.href}
              className={slots.item({
                link: true,
                className: `${classNameItem} ${item.className}`,
                itemActive: activeItem[level] === itemKey,
                itemGroup: isGroup
              })}
              onMouseEnter={() =>
                onActiveKeyChange(itemKey, checkChildren(item))
              }
              onTouchEnd={(e) => {
                e.stopPropagation();
                if (item.children && item.children?.length > 0) {
                  e.preventDefault();
                  onActiveKeyChange(itemKey, checkChildren(item));
                }
              }}
              onClick={hideMenu}
            >
              {menuNode}
              {childrenNode && (
                <UiModPortalBase
                  getContainer={() =>
                    document.getElementById(`UiAppMenuBase-children-${level}`)
                  }
                >
                  <div
                    className={
                      level !== 1 ? slots.children() : slots.subchildren()
                    }
                  >
                    {childrenNode}
                  </div>
                </UiModPortalBase>
              )}
            </Link>
          );
        })}
    </>
  );
};
